// Dashboard Service
// Este servicio maneja las estadísticas del dashboard del profesor

import ApiService from './ApiService';

class DashboardService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7156/api';
  }

  toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  formatShortDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit'
    });
  }

  formatDateTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Obtener estadísticas del dashboard del profesor
  async getEstadisticas() {
    try {
      
      // Debug del estado del token antes de hacer la petición
      ApiService.debugTokenStatus();
      
      const response = await ApiService.get('/DashboardProfesor/estadisticas');
      
      // Log de la respuesta recibida
      
      return response;
      
    } catch (error) {
      console.error('❌ Error al obtener estadísticas del dashboard:', error);
      
      // Manejo de errores específicos
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // No hacer nada aquí ya que el ApiService ya maneja la redirección automática
        throw error; // Re-lanzar el error para que el componente pueda manejarlo si es necesario
      } else if (error.message.includes('403')) {
        throw new Error('Acceso denegado. Contacta al administrador del sistema');
      } else if (error.message.includes('404')) {
        throw new Error('Endpoint de estadísticas no encontrado');
      } else if (error.message.includes('500')) {
        throw new Error('Error del servidor al obtener estadísticas');
      } else if (error.message.includes('Network')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet');
      }
      
      throw error;
    }
  }

  // Formatear datos para gráficos
  formatearDatosParaGraficos(estadisticas) {
    try {
      const actividadSemanal = (estadisticas.actividadPorDia || []).map((dia) => {
        const threads = this.toNumber(dia.threadsCreados);
        const clasicos = this.toNumber(dia.conversacionesClasicas);
        const foro = this.toNumber(dia.mensajesForo);
        const archivos = this.toNumber(dia.archivosSubidos);
        const total = threads + clasicos + foro + archivos;

        return {
          fecha: this.formatShortDate(dia.fecha),
          fechaCompleta: dia.fecha,
          threads,
          clasicos,
          foro,
          archivos,
          total
        };
      });

      const maxActividadDiaria = Math.max(...actividadSemanal.map((dia) => dia.total), 1);

      const usosPorAsistente = (estadisticas.usosPorAsistente || []).map((uso) => ({
        id: uso.idOpenAIAssistant,
        nombre: uso.nombreAsistente || 'Asistente',
        tipo: uso.tipoAsistente || 'OpenAI',
        esClasico: Boolean(uso.esGPTClasico),
        interacciones: this.toNumber(uso.totalInteracciones),
        threads: this.toNumber(uso.totalThreads),
        conversacionesClasicas: this.toNumber(uso.totalConversacionesClasicas),
        mensajesClasicos: this.toNumber(uso.totalMensajesClasicos),
        ultimoUso: uso.ultimoUso || null
      }));

      const maxUsoAsistente = Math.max(...usosPorAsistente.map((item) => item.interacciones), 1);
      const topAsistentes = usosPorAsistente
        .map((item) => ({
          ...item,
          porcentaje: Math.round((item.interacciones / maxUsoAsistente) * 100)
        }))
        .slice(0, 6);

      const topCursos = (estadisticas.topCursos || []).map((curso) => ({
        id: curso.idCurso,
        nombre: curso.nombreCurso || 'Curso',
        cicloLectivo: this.toNumber(curso.cicloLectivo),
        alumnos: this.toNumber(curso.totalAlumnos),
        materias: this.toNumber(curso.totalMaterias),
        aulas: this.toNumber(curso.totalAulas),
        mensajesForo: this.toNumber(curso.totalMensajesForo),
        archivos: this.toNumber(curso.totalArchivos),
        ultimaActividad: curso.ultimaActividad || null
      }));

      const maxAlumnosCurso = Math.max(...topCursos.map((curso) => curso.alumnos), 1);
      const topCursosConRatio = topCursos.map((curso) => ({
        ...curso,
        porcentajeAlumnos: Math.round((curso.alumnos / maxAlumnosCurso) * 100)
      }));

      const topAulas = (estadisticas.topAulas || []).map((aula) => {
        const scoreActividad = this.toNumber(aula.totalMensajesForo) + this.toNumber(aula.totalRespuestas) + this.toNumber(aula.totalArchivos);
        return {
          id: aula.idAula,
          nombre: aula.nombreAula || 'Aula',
          curso: aula.nombreCurso || 'Sin curso',
          materia: aula.nombreMateria || 'Sin materia',
          codigoMateria: aula.codigoMateria || '',
          archivos: this.toNumber(aula.totalArchivos),
          mensajesForo: this.toNumber(aula.totalMensajesForo),
          respuestas: this.toNumber(aula.totalRespuestas),
          scoreActividad,
          ultimaActividad: aula.ultimaActividad || null
        };
      });

      const maxActividadAula = Math.max(...topAulas.map((aula) => aula.scoreActividad), 1);
      const topAulasConRatio = topAulas.map((aula) => ({
        ...aula,
        porcentajeActividad: Math.round((aula.scoreActividad / maxActividadAula) * 100)
      }));

      const actividadReciente = (estadisticas.actividadReciente || []).map((item) => ({
        tipo: item.tipo || 'sistema',
        titulo: item.titulo || 'Actividad',
        descripcion: item.descripcion || '',
        meta: item.meta || '',
        fecha: item.fecha || null,
        fechaFormateada: this.formatDateTime(item.fecha)
      }));

      const resumenPrincipal = {
        totalCursos: this.toNumber(estadisticas.totalCursosActivos),
        totalMaterias: this.toNumber(estadisticas.totalMateriasAsignadas),
        totalAulas: this.toNumber(estadisticas.totalAulasActivas),
        totalAlumnos: this.toNumber(estadisticas.totalAlumnosUnicos),
        promedioAlumnosPorCurso: this.toNumber(estadisticas.promedioAlumnosPorCurso),
        totalArchivos: this.toNumber(estadisticas.totalArchivosAula),
        totalMensajesForo: this.toNumber(estadisticas.totalMensajesForo),
        totalRespuestasForo: this.toNumber(estadisticas.totalRespuestasForo),
        totalAsistentes: this.toNumber(estadisticas.totalAsistentesAsignados),
        totalAsistentesOpenAI: this.toNumber(estadisticas.totalAsistentesOpenAI),
        totalAsistentesClasicos: this.toNumber(estadisticas.totalAsistentesClasicos),
        totalThreads: this.toNumber(estadisticas.totalThreadsCreados),
        threadsHoy: this.toNumber(estadisticas.threadsActivosHoy),
        threadsSemana: this.toNumber(estadisticas.threadsActivosEstaSemana),
        threadsMes: this.toNumber(estadisticas.threadsActivosEsteMes),
        totalConversacionesClasicas: this.toNumber(estadisticas.totalConversacionesGPTClasico),
        conversacionesClasicasHoy: this.toNumber(estadisticas.conversacionesGPTClasicoHoy),
        conversacionesClasicasSemana: this.toNumber(estadisticas.conversacionesGPTClasicoEstaSemana),
        conversacionesClasicasMes: this.toNumber(estadisticas.conversacionesGPTClasicoEsteMes),
        totalMensajesClasicos: this.toNumber(estadisticas.totalMensajesGPTClasico),
        mensajesClasicosHoy: this.toNumber(estadisticas.mensajesGPTClasicoHoy),
        mensajesForoHoy: this.toNumber(estadisticas.mensajesForoHoy),
        archivosHoy: this.toNumber(estadisticas.archivosSubidosHoy),
        respuestasHoy: this.toNumber(estadisticas.respuestasForoHoy),
        asistenteMasUsado: estadisticas.asistenteMasUsado || 'Ninguno',
        aulaMasActiva: estadisticas.aulaMasActiva || 'Sin actividad',
        cursoConMasAlumnos: estadisticas.cursoConMasAlumnos || 'Sin cursos'
      };

      const pulsoHoy = {
        actividadHoy: resumenPrincipal.threadsHoy + resumenPrincipal.conversacionesClasicasHoy + resumenPrincipal.mensajesForoHoy + resumenPrincipal.archivosHoy,
        asistentesHoy: resumenPrincipal.threadsHoy + resumenPrincipal.conversacionesClasicasHoy,
        foroHoy: resumenPrincipal.mensajesForoHoy,
        archivosHoy: resumenPrincipal.archivosHoy,
        respuestasHoy: resumenPrincipal.respuestasHoy
      };

      return {
        actividadSemanal,
        maxActividadDiaria,
        usosPorAsistente,
        topAsistentes,
        topCursos: topCursosConRatio,
        topAulas: topAulasConRatio,
        actividadReciente,
        resumenPrincipal,
        pulsoHoy,
        fechaPrimerThread: estadisticas.fechaPrimerThread,
        fechaUltimoThread: estadisticas.fechaUltimoThread
      };

    } catch (error) {
      console.error('❌ Error al formatear datos para gráficos:', error);
      
      // Retornar datos por defecto en caso de error
      return {
        actividadSemanal: [],
        maxActividadDiaria: 0,
        usosPorAsistente: [],
        topAsistentes: [],
        topCursos: [],
        topAulas: [],
        actividadReciente: [],
        resumenPrincipal: {
          totalCursos: 0,
          totalMaterias: 0,
          totalAulas: 0,
          totalAlumnos: 0,
          promedioAlumnosPorCurso: 0,
          totalArchivos: 0,
          totalMensajesForo: 0,
          totalRespuestasForo: 0,
          totalAsistentes: 0,
          totalAsistentesOpenAI: 0,
          totalAsistentesClasicos: 0,
          totalThreads: 0,
          threadsHoy: 0,
          threadsSemana: 0,
          threadsMes: 0,
          totalConversacionesClasicas: 0,
          conversacionesClasicasHoy: 0,
          conversacionesClasicasSemana: 0,
          conversacionesClasicasMes: 0,
          totalMensajesClasicos: 0,
          mensajesClasicosHoy: 0,
          mensajesForoHoy: 0,
          archivosHoy: 0,
          respuestasHoy: 0,
          asistenteMasUsado: 'Ninguno'
        },
        pulsoHoy: {
          actividadHoy: 0,
          asistentesHoy: 0,
          foroHoy: 0,
          archivosHoy: 0,
          respuestasHoy: 0
        },
        fechaPrimerThread: null,
        fechaUltimoThread: null
      };
    }
  }

  // Calcular tendencias y métricas adicionales
  calcularTendencias(datosFormateados) {
    try {
      const { actividadSemanal, resumenPrincipal } = datosFormateados;
      
      const totalActividadSemana = actividadSemanal.reduce((sum, dia) => sum + dia.total, 0);
      const promedioDiario = actividadSemanal.length > 0 ? 
        Math.round((totalActividadSemana / actividadSemanal.length) * 10) / 10 : 0;

      const diaMasActivo = actividadSemanal.reduce((max, dia) => 
        dia.total > max.total ? dia : max, 
        { fecha: 'Ninguno', total: 0, threads: 0, clasicos: 0, foro: 0, archivos: 0 }
      );

      const canales = [
        { key: 'openai', label: 'OpenAI', total: resumenPrincipal.totalThreads },
        { key: 'clasico', label: 'Asistente IA', total: resumenPrincipal.totalConversacionesClasicas },
        { key: 'foro', label: 'Foro', total: resumenPrincipal.totalMensajesForo },
        { key: 'archivos', label: 'Archivos', total: resumenPrincipal.totalArchivos }
      ];
      const canalPrincipal = canales.reduce((max, item) => item.total > max.total ? item : max, canales[0] || { key: 'openai', label: 'OpenAI', total: 0 });
      const coberturaAulas = resumenPrincipal.totalAulas > 0
        ? Math.round(((datosFormateados.topAulas.filter((item) => item.scoreActividad > 0).length || 0) / resumenPrincipal.totalAulas) * 100)
        : 0;
      const promedioArchivosPorAula = resumenPrincipal.totalAulas > 0
        ? Math.round((resumenPrincipal.totalArchivos / resumenPrincipal.totalAulas) * 10) / 10
        : 0;
      const intensidadForo = resumenPrincipal.totalMensajesForo > 0
        ? Math.round((resumenPrincipal.totalRespuestasForo / resumenPrincipal.totalMensajesForo) * 100)
        : 0;

      return {
        promedioDiario,
        diaMasActivo,
        canalPrincipal,
        coberturaAulas,
        promedioArchivosPorAula,
        intensidadForo,
        totalActividadSemana
      };

    } catch (error) {
      console.error('❌ Error al calcular tendencias:', error);
      return {
        promedioDiario: 0,
        diaMasActivo: { fecha: 'Ninguno', total: 0, threads: 0, clasicos: 0, foro: 0, archivos: 0 },
        canalPrincipal: { key: 'openai', label: 'OpenAI', total: 0 },
        coberturaAulas: 0,
        promedioArchivosPorAula: 0,
        intensidadForo: 0,
        totalActividadSemana: 0
      };
    }
  }
}

// Crear instancia singleton
const dashboardService = new DashboardService();

export default dashboardService;
